import { ArrowRight, BellRing, CalendarDays, Clock3, HeartHandshake, MapPin, Sparkles, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import { useContent } from "../context/ContentContext";
import { NewsCard } from "../components/NewsCard";
import { PublicPageSkeleton } from "../components/LoadingSkeleton";
import { headerImageStyle } from "../lib/headerImages";

const valueIcons = [HeartHandshake, Sparkles, Trophy];

export function HomePage() {
  const { content, news, loading } = useContent();
  const { hero, home, training } = content;

  if (loading) return <PublicPageSkeleton variant="home" />;

  return (
    <>
      <section className="hero-section" style={headerImageStyle(content.headers.home)}>
        <img className="hero-image" src={content.headers.home.image || hero.image} alt="Tischtennisschläger und Ball auf einer Platte" />
        <div className="hero-overlay" />
        <div className="hero-content page-width">
          <span className="hero-kicker">{hero.eyebrow}</span>
          <h1>{hero.title}</h1>
          <p>{hero.lead}</p>
          <div className="hero-actions">
            <Link to="/training" className="button button--accent">{hero.primaryLabel} <ArrowRight size={18} /></Link>
            <Link to="/aktuelles" className="button button--ghost">{hero.secondaryLabel}</Link>
          </div>
        </div>
      </section>

      {home.noticeVisible && (
        <section id="willkommen" className={`welcome-strip page-width ${home.noticeImage ? "welcome-strip--with-image" : ""}`}>
          <div className="welcome-grid">
            {home.noticeImage && <img className="welcome-image" src={home.noticeImage} alt="Hinweis des TTC Rehlingen" loading="lazy" />}
            <span className="welcome-icon"><Sparkles /></span>
            <div><span className="section-kicker">Willkommen beim TTC</span><h2>{home.noticeTitle}</h2></div>
            <p>{home.noticeText}</p>
            {home.noticeButtonLabel.trim() && <a className="button button--teal" href={home.noticeButtonHref || "/kontakt"}>{home.noticeButtonLabel} <ArrowRight size={17} /></a>}
          </div>
        </section>
      )}

      {home.highlightVisible && (
        <section className="home-highlight-section page-width">
          <article className={`home-highlight-card ${home.highlightImage ? "home-highlight-card--with-image" : ""}`}>
            {home.highlightImage && <img src={home.highlightImage} alt="Aktueller Hinweis des TTC Rehlingen" loading="lazy" />}
            <div className="home-highlight-copy">
              <span className="home-highlight-label"><BellRing /> Wichtiger Hinweis</span>
              <h2>{home.highlightTitle}</h2>
              <p>{home.highlightText}</p>
              {home.highlightButtonVisible && home.highlightButtonLabel.trim() && <a className="button button--light" href={home.highlightButtonHref || "/training"}>{home.highlightButtonLabel} <ArrowRight size={17} /></a>}
            </div>
          </article>
        </section>
      )}

      <section className="section page-width home-news-section">
        <div className="section-heading split-heading news-heading">
          <div><span className="section-kicker">Immer auf dem Laufenden</span><h2>Aktuelles vom TTC</h2></div>
          <Link className="text-link text-link--large" to="/aktuelles">Alle Beiträge <ArrowRight size={18} /></Link>
        </div>
        <div className="home-news-grid">
          {news.slice(0, 3).map((post, index) => <NewsCard key={post.id} post={post} featured={index === 0} />)}
        </div>
      </section>

      <section className="section page-width intro-grid">
        <div className="club-gallery">
          <img className="club-gallery-main" src="/images/halle.jpg" alt="Tischtennishalle des TTC Rehlingen" />
          <img className="club-gallery-small" src="/images/doppelsieg.jpeg" alt="Mannschaft des TTC Rehlingen" />
          <span>Seit 1964<br />in Rehlingen</span>
        </div>
        <div className="intro-copy">
          <span className="section-kicker">Unser Verein</span>
          <h2>{home.aboutTitle}</h2>
          <p>{home.aboutText}</p>
          <div className="stats-row">
            {home.stats.map((stat) => <div key={stat.label}><strong>{stat.value}</strong><span>{stat.label}</span></div>)}
          </div>
          <Link className="text-link text-link--large" to="/verein">Den Verein kennenlernen <ArrowRight size={18} /></Link>
        </div>
      </section>

      <section className="training-banner">
        <div className="page-width training-banner-grid">
          <div>
            <span className="section-kicker section-kicker--light">Training</span>
            <h2>{training.title}</h2>
            <p>{training.intro}</p>
            <div className="location-line"><MapPin size={18} /> {training.location}, {training.address}</div>
          </div>
          <div className="schedule-card">
            {training.slots.map((slot) => (
              <div className="schedule-day" key={slot.day}>
                <div><CalendarDays /><strong>{slot.day}</strong></div>
                <dl>
                  <div><dt>Jugend</dt><dd>{slot.youth}</dd></div>
                  <div><dt>Erwachsene</dt><dd>{slot.adults}</dd></div>
                </dl>
              </div>
            ))}
            <Link to="/training" className="button button--dark">Alle Trainingsinfos <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      <section className="section section--soft">
        <div className="page-width">
          <div className="section-heading split-heading">
            <div><span className="section-kicker">Das zeichnet uns aus</span><h2>Mehr als Tischtennis.</h2></div>
            <p>Wir schaffen einen Ort, an dem Bewegung, Entwicklung und Gemeinschaft selbstverständlich zusammengehören.</p>
          </div>
          <div className="values-grid">
            {home.values.map((value, index) => {
              const Icon = valueIcons[index] || Clock3;
              return <article key={value.title}><span className="value-icon"><Icon /></span><h3>{value.title}</h3><p>{value.text}</p></article>;
            })}
          </div>
        </div>
      </section>

      <section className="page-width join-card">
        <div><span className="section-kicker section-kicker--light">Einfach ausprobieren</span><h2>Dein erster Ballwechsel wartet.</h2></div>
        <p>Schreib uns kurz und komm zu einem unverbindlichen Probetraining vorbei.</p>
        <Link className="button button--light" to="/kontakt">Kontakt aufnehmen <ArrowRight size={18} /></Link>
      </section>
    </>
  );
}
